import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart3,
  Calendar,
  Clock,
  FileText,
  RefreshCw,
  Search,
  Shield,
  Users,
} from 'lucide-react';
import {
  fetchAdminUsers,
  fetchAllUsers,
  fetchPageViewsInRange,
  fetchSessionsInRange,
  fetchUserActivity,
  searchUsersByName,
} from '../../services/analyticsAdminService';
import {
  DATE_PRESETS,
  distinctPagePaths,
  estimateTimeOnPages,
  filterSessions,
  formatDateKey,
  formatDuration,
  getDateRangeForPreset,
  indexPageViewsBySession,
  medianSessionDurationSeconds,
  parseDurationSeconds,
  sessionPageCount,
  signupsByDay,
  toIsoRange,
  countUniqueVisitors,
  uniqueVisitorsByDay,
} from '../../utils/analyticsMetrics';

const presetLabels = {
  [DATE_PRESETS.today]: 'Today',
  [DATE_PRESETS.last7]: 'Last 7 days',
  [DATE_PRESETS.last30]: 'Last 30 days',
  [DATE_PRESETS.ytd]: 'Year to date',
  [DATE_PRESETS.custom]: 'Custom range',
};

const MiniBarChart = ({ rows, valueKey, labelKey = 'date', maxBars = 14 }) => {
  const slice = rows.slice(-maxBars);
  const maxVal = Math.max(1, ...slice.map((r) => r[valueKey] || 0));

  if (slice.length === 0) {
    return <p className="text-sm text-stone-500">No data in this period.</p>;
  }

  return (
    <div className="space-y-2">
      {slice.map((row) => (
        <div key={row[labelKey]} className="flex items-center gap-3 text-sm">
          <span className="w-24 shrink-0 text-stone-500 tabular-nums">{row[labelKey]}</span>
          <div className="flex-1 h-6 bg-stone-100 rounded-md overflow-hidden">
            <div
              className="h-full bg-indigo-600 rounded-md transition-all"
              style={{ width: `${((row[valueKey] || 0) / maxVal) * 100}%` }}
            />
          </div>
          <span className="w-10 text-right font-medium tabular-nums">{row[valueKey]}</span>
        </div>
      ))}
    </div>
  );
};

const AdminAnalyticsPage = ({ isAdmin, setMessage }) => {
  const [preset, setPreset] = useState(DATE_PRESETS.last7);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [minDuration, setMinDuration] = useState('');
  const [maxDuration, setMaxDuration] = useState('');
  const [minPages, setMinPages] = useState('');
  const [maxPages, setMaxPages] = useState('');
  const [pagePathFilter, setPagePathFilter] = useState('');

  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [pageViews, setPageViews] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);

  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchContainerRef = useRef(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userActivity, setUserActivity] = useState(null);
  const [userActivityLoading, setUserActivityLoading] = useState(false);

  const { start, end } = useMemo(
    () => getDateRangeForPreset(preset, customStart, customEnd),
    [preset, customStart, customEnd]
  );
  const { startIso, endIso } = useMemo(() => toIsoRange(start, end), [start, end]);

  const loadDashboard = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const [sessionData, viewData, usersData, adminsData] = await Promise.all([
        fetchSessionsInRange(startIso, endIso),
        fetchPageViewsInRange(startIso, endIso),
        fetchAllUsers(),
        fetchAdminUsers(),
      ]);
      setSessions(sessionData);
      setPageViews(viewData);
      setAllUsers(usersData);
      setAdminUsers(adminsData);
    } catch (err) {
      console.error('[ANALYTICS ADMIN]', err);
      setMessage?.(
        err.message?.includes('policy') || err.code === '42501'
          ? 'Cannot load analytics. Run supabase-admin-analytics-rls.sql and sign in as admin.'
          : 'Failed to load analytics data.'
      );
      setTimeout(() => setMessage?.(''), 5000);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, startIso, endIso, setMessage]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const pageViewsBySession = useMemo(
    () => indexPageViewsBySession(pageViews),
    [pageViews]
  );

  const filters = useMemo(
    () => ({
      minDurationSeconds: minDuration ? Number(minDuration) : 0,
      maxDurationSeconds: maxDuration ? Number(maxDuration) : null,
      minPages: minPages ? Number(minPages) : 0,
      maxPages: maxPages ? Number(maxPages) : null,
      pagePath: pagePathFilter,
    }),
    [minDuration, maxDuration, minPages, maxPages, pagePathFilter]
  );

  const filteredSessions = useMemo(
    () => filterSessions(sessions, pageViewsBySession, filters),
    [sessions, pageViewsBySession, filters]
  );

  const allowedSessionIds = useMemo(
    () => new Set(filteredSessions.map((s) => s.session_id)),
    [filteredSessions]
  );

  const visitorsSeries = useMemo(
    () => uniqueVisitorsByDay(pageViews, allowedSessionIds),
    [pageViews, allowedSessionIds]
  );

  const signupSeries = useMemo(
    () => signupsByDay(allUsers, start, end),
    [allUsers, start, end]
  );

  const uniqueVisitorsTotal = useMemo(
    () => countUniqueVisitors(pageViews, allowedSessionIds),
    [pageViews, allowedSessionIds]
  );

  const medianSessionDuration = useMemo(
    () => medianSessionDurationSeconds(filteredSessions),
    [filteredSessions]
  );

  const pagePathOptions = useMemo(() => distinctPagePaths(pageViews), [pageViews]);

  useEffect(() => {
    const q = userSearch.trim();
    if (q.length < 1) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }

    setSearchLoading(true);
    setSearchOpen(true);
    const timer = setTimeout(async () => {
      try {
        const results = await searchUsersByName(q, 5);
        setSearchResults(results);
      } catch (err) {
        console.error(err);
        setSearchResults([]);
        setMessage?.('User search failed.');
        setTimeout(() => setMessage?.(''), 3000);
      } finally {
        setSearchLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [userSearch, setMessage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectSearchUser = (user) => {
    setSelectedUser(user);
    setUserSearch(user.name || '');
    setSearchOpen(false);
  };

  useEffect(() => {
    if (!selectedUser?.id || !isAdmin) {
      setUserActivity(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setUserActivityLoading(true);
      try {
        const activity = await fetchUserActivity(selectedUser.id, startIso, endIso);
        if (!cancelled) setUserActivity(activity);
      } catch (err) {
        console.error(err);
        if (!cancelled) setMessage?.('Failed to load user activity.');
      } finally {
        if (!cancelled) setUserActivityLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedUser, startIso, endIso, isAdmin, setMessage]);

  const userSessionsFiltered = useMemo(() => {
    if (!userActivity?.sessions) return [];
    return filterSessions(userActivity.sessions, indexPageViewsBySession(userActivity.pageViews), filters);
  }, [userActivity, filters]);

  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto panel p-8 text-center text-stone-600">
        Admin access required.
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-12">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-serif font-semibold text-stone-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-indigo-600" />
            Analytics
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            Unique visitors, sessions, signups, and per-user activity
          </p>
        </div>
        <button
          type="button"
          onClick={loadDashboard}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Date range */}
      <section className="panel p-6 space-y-4">
        <h2 className="text-sm font-semibold text-stone-800 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-indigo-600" />
          Time period
        </h2>
        <div className="flex flex-wrap gap-2">
          {Object.entries(presetLabels).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setPreset(key)}
              className={`px-3 py-1.5 text-sm rounded-full border transition ${
                preset === key
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-stone-600 border-stone-200 hover:border-stone-400'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {preset === DATE_PRESETS.custom && (
          <div className="flex flex-wrap gap-4 items-end">
            <label className="text-sm">
              <span className="block text-stone-600 mb-1">Start</span>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="border border-stone-300 rounded-lg px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="block text-stone-600 mb-1">End</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="border border-stone-300 rounded-lg px-3 py-2"
              />
            </label>
          </div>
        )}
        <p className="text-xs text-stone-500">
          Showing {formatDateKey(start)} — {formatDateKey(end)}
        </p>
      </section>

      {/* Session filters */}
      <section className="panel p-6 space-y-4">
        <h2 className="text-sm font-semibold text-stone-800 flex items-center gap-2">
          <Clock className="w-4 h-4 text-indigo-600" />
          Session filters
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <label className="text-sm">
            <span className="block text-stone-600 mb-1">Min visit length (sec)</span>
            <input
              type="number"
              min="0"
              value={minDuration}
              onChange={(e) => setMinDuration(e.target.value)}
              className="w-full border border-stone-300 rounded-lg px-3 py-2"
              placeholder="0"
            />
          </label>
          <label className="text-sm">
            <span className="block text-stone-600 mb-1">Max visit length (sec)</span>
            <input
              type="number"
              min="0"
              value={maxDuration}
              onChange={(e) => setMaxDuration(e.target.value)}
              className="w-full border border-stone-300 rounded-lg px-3 py-2"
              placeholder="Any"
            />
          </label>
          <label className="text-sm">
            <span className="block text-stone-600 mb-1">Min pages visited</span>
            <input
              type="number"
              min="0"
              value={minPages}
              onChange={(e) => setMinPages(e.target.value)}
              className="w-full border border-stone-300 rounded-lg px-3 py-2"
              placeholder="0"
            />
          </label>
          <label className="text-sm">
            <span className="block text-stone-600 mb-1">Max pages visited</span>
            <input
              type="number"
              min="0"
              value={maxPages}
              onChange={(e) => setMaxPages(e.target.value)}
              className="w-full border border-stone-300 rounded-lg px-3 py-2"
              placeholder="Any"
            />
          </label>
          <label className="text-sm lg:col-span-1">
            <span className="block text-stone-600 mb-1">Page visited</span>
            <select
              value={pagePathFilter}
              onChange={(e) => setPagePathFilter(e.target.value)}
              className="w-full border border-stone-300 rounded-lg px-3 py-2"
            >
              <option value="">Any page</option>
              {pagePathOptions.map((path) => (
                <option key={path} value={path}>
                  {path}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Unique visitors', value: uniqueVisitorsTotal, icon: Users },
              { label: 'Sessions (filtered)', value: filteredSessions.length, icon: BarChart3 },
              { label: 'Median session length', value: formatDuration(medianSessionDuration), icon: Clock },
              {
                label: 'New accounts',
                value: signupSeries.reduce((n, r) => n + r.signups, 0),
                icon: Users,
              },
            ].map(({ label, value, icon: Icon }) => (
              <div key={label} className="panel p-5">
                <div className="flex items-center gap-2 text-stone-500 text-sm mb-2">
                  <Icon className="w-4 h-4" />
                  {label}
                </div>
                <p className="text-2xl font-semibold text-stone-900 tabular-nums">{value}</p>
              </div>
            ))}
          </div>

          <p className="text-xs text-stone-500 panel px-4 py-3">
            Unique visitors use each browser&apos;s <strong>client ID</strong> (anonymous) or{' '}
            <strong>account ID</strong> when logged in—not IP address. IPs are stored for location
            only; shared networks and VPNs make IP a poor unique-visitor signal.
          </p>

          <div className="grid lg:grid-cols-2 gap-6">
            <section className="panel p-6">
              <h3 className="text-sm font-semibold text-stone-800 mb-4">Unique visitors per day</h3>
              <MiniBarChart rows={visitorsSeries} valueKey="uniqueVisitors" />
            </section>
            <section className="panel p-6">
              <h3 className="text-sm font-semibold text-stone-800 mb-4">Account signups per day</h3>
              <MiniBarChart rows={signupSeries} valueKey="signups" />
            </section>
          </div>

          {/* Admin accounts */}
          <section className="panel p-6">
            <h3 className="text-sm font-semibold text-stone-800 mb-4 flex items-center gap-2">
              <Shield className="w-4 h-4 text-indigo-600" />
              Admin accounts
            </h3>
            {adminUsers.length === 0 ? (
              <p className="text-sm text-stone-500">No admin users found.</p>
            ) : (
              <ul className="divide-y divide-stone-100">
                {adminUsers.map((u) => (
                  <li key={u.id} className="py-2 flex justify-between text-sm">
                    <span className="font-medium text-stone-800">{u.name || 'Unnamed'}</span>
                    <span className="text-stone-500 text-xs">
                      since {u.created_at ? formatDateKey(u.created_at) : '—'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* User lookup */}
          <section className="panel p-6 space-y-4">
            <h3 className="text-sm font-semibold text-stone-800 flex items-center gap-2">
              <Search className="w-4 h-4 text-indigo-600" />
              User activity lookup
            </h3>
            <div ref={searchContainerRef} className="relative max-w-md">
              <label htmlFor="admin-user-search" className="sr-only">
                Search users by display name
              </label>
              <input
                id="admin-user-search"
                type="search"
                autoComplete="off"
                role="combobox"
                aria-expanded={searchOpen && userSearch.trim().length > 0}
                aria-controls="admin-user-search-listbox"
                value={userSearch}
                onChange={(e) => {
                  setUserSearch(e.target.value);
                  setSelectedUser(null);
                  setSearchOpen(true);
                }}
                onFocus={() => {
                  if (userSearch.trim()) setSearchOpen(true);
                }}
                placeholder="Start typing a display name…"
                className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
              />
              {searchOpen && userSearch.trim().length > 0 && (
                <ul
                  id="admin-user-search-listbox"
                  role="listbox"
                  className="absolute z-10 mt-1 w-full bg-white border border-stone-200 rounded-lg shadow-lg overflow-hidden"
                >
                  {searchLoading ? (
                    <li className="px-3 py-2 text-sm text-stone-500">Searching…</li>
                  ) : searchResults.length === 0 ? (
                    <li className="px-3 py-2 text-sm text-stone-500">No matching accounts</li>
                  ) : (
                    searchResults.map((u) => (
                      <li key={u.id} role="option">
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-stone-50 focus:bg-stone-50 focus:outline-none"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleSelectSearchUser(u)}
                        >
                          <span className="font-medium text-stone-800">{u.name || 'Unnamed'}</span>
                          {u.is_admin && (
                            <span className="ml-2 text-xs text-indigo-600">Admin</span>
                          )}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>

            {selectedUser && (
              <div className="border-t border-stone-200 pt-4 space-y-4">
                <p className="text-sm">
                  <span className="font-semibold">{selectedUser.name}</span>
                  <span className="text-stone-500 ml-2">
                    Joined {formatDateKey(selectedUser.created_at)}
                  </span>
                </p>

                {userActivityLoading ? (
                  <p className="text-sm text-stone-500">Loading activity…</p>
                ) : userActivity ? (
                  <>
                    <p className="text-sm text-stone-600">
                      {userSessionsFiltered.length} session(s) in range ·{' '}
                      {userActivity.pageViews?.length || 0} page view(s)
                    </p>

                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-left text-stone-500 border-b">
                            <th className="py-2 pr-4">Session start</th>
                            <th className="py-2 pr-4">Duration</th>
                            <th className="py-2 pr-4">Pages</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userSessionsFiltered.slice(0, 20).map((s) => (
                            <tr key={s.session_id} className="border-b border-stone-50">
                              <td className="py-2 pr-4 tabular-nums">
                                {new Date(s.started_at).toLocaleString()}
                              </td>
                              <td className="py-2 pr-4">{formatDuration(parseDurationSeconds(s))}</td>
                              <td className="py-2 pr-4">
                                {sessionPageCount(s, indexPageViewsBySession(userActivity.pageViews))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <h4 className="text-sm font-semibold flex items-center gap-2 pt-2">
                      <FileText className="w-4 h-4" />
                      Time per page (estimated from next view)
                    </h4>
                    {userSessionsFiltered.slice(0, 5).map((s) => {
                      const views = (userActivity.pageViews || []).filter(
                        (pv) => pv.session_id === s.session_id
                      );
                      const times = estimateTimeOnPages(views, s.ended_at);
                      return (
                        <div key={s.session_id} className="text-sm bg-stone-50 rounded-lg p-3 space-y-1">
                          <p className="font-medium text-stone-700">
                            Session {new Date(s.started_at).toLocaleString()}
                          </p>
                          {times.length === 0 ? (
                            <p className="text-stone-500">No page views recorded.</p>
                          ) : (
                            <ul className="space-y-1">
                              {times.map((row, idx) => (
                                <li key={`${row.page_path}-${idx}`} className="flex justify-between gap-4">
                                  <span className="truncate text-stone-600">{row.page_path}</span>
                                  <span className="shrink-0 tabular-nums">
                                    {formatDuration(row.seconds_on_page)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </>
                ) : null}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default AdminAnalyticsPage;
